import { defineAsyncComponent } from "vue";
import * as Vue from "vue/dist/vue.esm-bundler";
import { i18nVue } from "laravel-vue-i18n";
import moment from "moment";
import "moment-timezone";
import "moment/dist/locale/es";
import Alpine from "alpinejs";
import VueGoogleCharts from "vue-google-charts";
import "./bootstrap";
import "./websocket/OrgMessageChannel";
import "vue-loading-skeleton/dist/style.css";
import "vue-multiselect/dist/vue-multiselect.css";
import Chart from "chart.js/auto";
import "chartjs-adapter-moment";
import zoomPlugin from "chartjs-plugin-zoom";
import { createPinia } from "pinia";
import * as ConfirmDialog from "vuejs-confirm-dialog";
import { DialogsWrapper } from "vuejs-confirm-dialog";
import { useUserStore } from "./vue/store/userStore";
import VueSimpleContextMenu from 'vue-simple-context-menu';
import 'vue-simple-context-menu/dist/vue-simple-context-menu.css';

/* import font awesome icon component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import tooltip from "./vue/directives/tooltip.js";
import clickOutside from "./vue/directives/click-outside.js";
// Component imports
import App from "./vue/App.vue";
import greeting from "./vue/greeting.vue";
import MachineDetails from "./vue/connect/machine/MachineDetails.vue";
import MachineIndex from "./vue/connect/machine/MachineIndex.vue";
import OrgESSUtility from "./vue/connect/org/partials/OrgESSUtility.vue";
import OrgIndex from "./vue/connect/org/OrgIndex.vue";
import UploadTitle from "./vue/connect/upload/UploadTitle.vue";
import UsageUploadModal from "./vue/connect/upload/UsageUploadModal.vue";
import RedirectUploadSdModal from "./vue/connect/upload/RedirectUploadSdModal.vue";
import PatientModal from "./vue/connect/patients/PatientModal.vue";
import userCreate from "./vue/connect/users/create.vue";
import userShow from "./vue/connect/users/show.vue";
import UserIndex from "./vue/connect/users/UserIndex.vue";
// import PatientSnapshotHeader from "./vue/connect/patients/PatientSnapshotHeader.vue";
import { provideGlobalStoreState } from "./vue/provideGlobalStoreState";
import AppChart from "./vue/shared/AppChart.vue";
import { refreshToken } from "@/utilities/auth";
import MissingTranslationsPlugin from "./vue/plugins/MissingTranslationsPlugin";

const app = Vue.createApp(App);

app.directive('tooltip', tooltip);
app.directive('clickoutside', clickOutside);
app.use(VueGoogleCharts)
app.use(createPinia())

const userStore = useUserStore();
if (window.location.pathname !== '/verify-login' && window.location.pathname !== '/login') {
    userStore.fetchUserInfo();
    userStore.fetchOrgInfo();
}

app.use(ConfirmDialog)
app.use(i18nVue, {
    resolve: async lang => {
        const langs = import.meta.glob("../../lang/*.json");
        return await langs[`../../lang/${lang}.json`]();
    }
});
app.use(MissingTranslationsPlugin);

provideGlobalStoreState(app);

app.component('vue-simple-context-menu', VueSimpleContextMenu);
app.component('vue-greeting', greeting);
app.component('vue-machine-details', MachineDetails);
app.component('vue-machine-index', MachineIndex);
app.component('vue-org-index', OrgIndex);
app.component('vue-upload-title', UploadTitle);
app.component('vue-upload-sd-modal', RedirectUploadSdModal);
app.component('vue-usage-upload-modal', UsageUploadModal);
app.component('vue-patient-modal', PatientModal);
app.component('vue-user-create', userCreate);
app.component('vue-user-show', userShow);
app.component('vue-user-index', UserIndex);
// app.component('vue-patient-snapshot-header', PatientSnapshotHeader);
app.component('vue-chart', AppChart);
app.component('vue-dialogs-wrapper', DialogsWrapper);
app.component('vue-org-ess-utility', OrgESSUtility);

/**
 * Dynamic component imports
 *
 * SEE https://vuejs.org/guide/best-practices/performance.html#code-splitting
 */
app.component('vue-analytics-dashboard', defineAsyncComponent(
    () => import("./vue/connect/powerbi/PowerBi.vue") // Analytics dashboard component
));
app.component('vue-analytics-blank', defineAsyncComponent(
    () => import("./vue/connect/powerbi/PowerBiBlank.vue") // Analytics blank dashboard component
));
app.component('vue-patient-detail', defineAsyncComponent(
    () => import("./vue/connect/patients/PatientDetail.vue") //
));
app.component('vue-patient-index', defineAsyncComponent(
    () => import("./vue/connect/patients/PatientIndex.vue") //
));
app.component('vue-org-detail', defineAsyncComponent(
    () => import("./vue/connect/org/OrgDetails.vue") //
));
app.component('vue-device-index', defineAsyncComponent(
    () => import("./vue/connect/devices/DeviceIndex.vue") //
));
app.component('vue-device-detail', defineAsyncComponent(
    () => import("./vue/connect/devices/DeviceDetail.vue") //
));
app.component('vue-device-detail-org-user', defineAsyncComponent(
    () => import("./vue/connect/devices/detail-for-user/DeviceDetail.vue") //
));
app.component('vue-dashboard-index', defineAsyncComponent(() => import("./vue/connect/dashboard/DashboardIndex.vue")));
app.component('vue-support-suppressed-email-index', defineAsyncComponent(() => import("./vue/support/suppressed-email/SuppressedEmailIndex.vue")));
app.component('vue-support-role-index', defineAsyncComponent(() => import("./vue/support/role/RoleIndex.vue")));
app.component('vue-support-role-show', defineAsyncComponent(() => import("./vue/support/role/RoleDisplay.vue")));
app.component('vue-support-mobile-user-index', defineAsyncComponent(() => import("./vue/support/mobile-user/UserInfoIndex.vue")));
app.component('vue-support-activity-log-index', defineAsyncComponent(() => import("./vue/support/activity-log/ActivityLogIndex.vue")));

app.component('vue-support-trusted-domains-index', defineAsyncComponent(() => import("./vue/support/trusted-domains/NewTrustedDomains.vue")));

app.component('vue-upload-detail', defineAsyncComponent(
    () => import("./vue/connect/upload/UploadDetail.vue") //
));
app.component('vue-upload-index', defineAsyncComponent(
    () => import("./vue/connect/upload/UploadIndex.vue") //
));
app.component('vue-vocson-patient-detail', defineAsyncComponent(
    () => import("./vue/connect/vocson/patients/PatientDetail.vue") //
));

app.component('vue-announcement-index', defineAsyncComponent(
    () => import("./vue/connect/announcement/AnnouncementsIndex.vue") //
));
app.component('vue-announcement-modal', defineAsyncComponent(
    () => import("./vue/connect/announcement/AnnouncementsModal.vue") //
));

app.component('font-awesome-icon', FontAwesomeIcon);

app.component('vue-activity-check-modal', defineAsyncComponent(
    () => import("./vue/shared/TheActivityCheckModal.vue")
))

window.vueApp = app.mount('#vueApp');

window.Vue = Vue;

window.Alpine = Alpine;

moment.tz.setDefault('America/New_York')
moment.locale('en');
window.moment = moment;

window.Chart = Chart;
Chart.register(zoomPlugin);

//Redirect 401 axios requests to logout
window.axios.interceptors.response.use(
    (response) => {

        if (![
            '/csrf-token',
            '/logout',
            '/login'
        ].includes(response.config.url)) {
            refreshToken();
        }
        return response;
    },
    (error) => {
        if (error.response) {
            if (error.response.status === 401 && !error.config.url.includes('/login')) {
                window.location.reload();
            }
        }
        return Promise.reject(error);
    },
);
// Line to Add/Remove for Deployment Testing
Alpine.start();

